import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Save, Play, Plus, Activity, ChevronDown, ChevronUp,
  Loader2, ToggleLeft, ToggleRight, AlertCircle, CheckCircle2,
} from 'lucide-react';
import { api } from '../api.js';
import { STEP_TYPES } from '../constants.js';
import Canvas from '../components/Canvas.jsx';
import StepModal from '../components/StepModal.jsx';
import StepConfigPanel from '../components/StepConfigPanel.jsx';
import TriggerConfig from '../components/TriggerConfig.jsx';
import LogsPanel from '../components/LogsPanel.jsx';

function uid() { return crypto.randomUUID(); }

// Default position for newly added steps
function nextPosition(steps) {
  if (!steps.length) return { x: 80, y: 220 };
  const last = steps[steps.length - 1];
  const pos  = last.position ?? { x: 80, y: 60 };
  return { x: pos.x, y: pos.y + 160 };
}

// ─── Toast notification ───────────────────────────────────────────────────────
function Toast({ message, type = 'success', onDismiss }) {
  useEffect(() => { const t = setTimeout(onDismiss, 3000); return () => clearTimeout(t); }, []);
  const bg = type === 'success' ? 'rgba(34,197,94,.12)' : 'rgba(239,68,68,.12)';
  const border = type === 'success' ? 'rgba(34,197,94,.3)' : 'rgba(239,68,68,.3)';
  const color  = type === 'success' ? 'var(--green)' : 'var(--red)';
  const Icon   = type === 'success' ? CheckCircle2 : AlertCircle;
  return (
    <div style={{
      position: 'fixed', bottom: 24, right: 24, zIndex: 100,
      display: 'flex', alignItems: 'center', gap: 10,
      background: bg, border: `1px solid ${border}`, color,
      borderRadius: 10, padding: '12px 18px',
      fontSize: '.875rem', fontWeight: 500,
      boxShadow: '0 8px 32px rgba(0,0,0,.5)',
      animation: 'fadeSlideIn .2s ease',
    }}>
      <Icon size={16} /> {message}
    </div>
  );
}

// ─── Inline editable title ────────────────────────────────────────────────────
function EditableTitle({ value, onChange }) {
  const [editing, setEditing] = useState(false);
  const [draft,   setDraft]   = useState(value);
  const inputRef = useRef();

  useEffect(() => { if (editing) inputRef.current?.select(); }, [editing]);
  useEffect(() => { setDraft(value); }, [value]);

  function commit() {
    setEditing(false);
    if (draft.trim()) onChange(draft.trim());
    else setDraft(value);
  }

  if (editing) return (
    <input
      ref={inputRef}
      value={draft}
      onChange={e => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') { setEditing(false); setDraft(value); } }}
      style={{
        fontFamily: 'IBM Plex Mono', fontSize: '1rem', fontWeight: 600,
        background: 'var(--surface-2)', border: '1px solid var(--primary)',
        borderRadius: 7, padding: '4px 10px', color: 'var(--text)',
        outline: 'none', minWidth: 200,
      }}
    />
  );

  return (
    <button
      onClick={() => setEditing(true)}
      style={{
        fontFamily: 'IBM Plex Mono', fontSize: '1rem', fontWeight: 600,
        color: 'var(--text)', background: 'none', border: 'none',
        cursor: 'text', padding: '4px 8px', borderRadius: 6,
      }}
      className="hover:bg-[#1E2330] transition-colors"
      title="Click to rename"
    >
      {value}
    </button>
  );
}

// ─── Main Editor ─────────────────────────────────────────────────────────────

export default function WorkflowEditor() {
  const { id }  = useParams();
  const nav     = useNavigate();
  const isNew   = !id;

  const [workflow,       setWorkflow]       = useState({
    name: 'New Workflow', description: '',
    trigger_type: 'webhook', trigger_config: {},
    steps: [], edges: [], is_active: 1,
  });
  const [loading,        setLoading]        = useState(!isNew);
  const [saving,         setSaving]         = useState(false);
  const [isDirty,        setIsDirty]        = useState(false);
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [showStepModal,  setShowStepModal]  = useState(false);
  const [showTrigger,    setShowTrigger]    = useState(true);
  const [toast,          setToast]          = useState(null);
  const [triggerLoading, setTriggerLoading] = useState(false);

  // Run/log panel
  const [activeRun,  setActiveRun]  = useState(null);
  const [runLogs,    setRunLogs]    = useState([]);
  const [showLogs,   setShowLogs]   = useState(false);

  // ── Load existing workflow ─────────────────────────────────────────────────
  useEffect(() => {
    if (isNew) return;
    (async () => {
      try {
        setLoading(true);
        const data = await api.workflows.get(id);
        setWorkflow(data.workflow);
      } catch (e) {
        showToast(e.message, 'error');
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  function showToast(message, type = 'success') {
    setToast({ message, type });
  }

  // ── Workflow mutation helpers ──────────────────────────────────────────────
  function update(patch) {
    setWorkflow(prev => ({ ...prev, ...patch }));
    setIsDirty(true);
  }

  // ── Save ──────────────────────────────────────────────────────────────────
  async function save() {
    try {
      setSaving(true);
      if (isNew) {
        const data = await api.workflows.create(workflow);
        setIsDirty(false);
        nav(`/workflows/${data.workflow.id}`, { replace: true });
        showToast('Workflow created');
      } else {
        const data = await api.workflows.update(id, workflow);
        setWorkflow(data.workflow);
        setIsDirty(false);
        showToast('Workflow saved');
      }
    } catch (e) {
      showToast(e.message, 'error');
      // Free-tier workflow cap → send them to upgrade.
      if (e.code === 'workflow_limit_reached') {
        setTimeout(() => nav('/settings'), 1200);
      }
    } finally {
      setSaving(false);
    }
  }

  // ── Toggle active ─────────────────────────────────────────────────────────
  async function toggleActive() {
    if (isNew) return;
    try {
      const data = await api.workflows.toggle(id);
      setWorkflow(prev => ({ ...prev, is_active: data.is_active }));
      showToast(data.is_active ? 'Workflow activated' : 'Workflow deactivated');
    } catch (e) { showToast(e.message, 'error'); }
  }

  // ── Manual trigger ────────────────────────────────────────────────────────
  async function manualTrigger() {
    if (isNew) { showToast('Save the workflow first', 'error'); return; }
    if (isDirty) { showToast('Save changes first', 'error'); return; }

    try {
      setTriggerLoading(true);
      setShowLogs(true);
      const result = await api.workflows.trigger(id);

      // Poll for run completion
      let run = null;
      for (let i = 0; i < 30; i++) {
        const d = await api.runs.get(result.runId);
        run = d.run;
        if (run.status !== 'running') break;
        await new Promise(r => setTimeout(r, 800));
      }

      setActiveRun(run);
      const logsData = await api.runs.logs(result.runId);
      setRunLogs(logsData.logs);

      showToast(`Run ${run.status}`, run.status === 'success' ? 'success' : 'error');
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setTriggerLoading(false);
    }
  }

  // ── Add step ──────────────────────────────────────────────────────────────
  function addStep(type) {
    setShowStepModal(false);
    const meta    = STEP_TYPES[type] ?? {};
    const stepId  = uid();
    const newStep = {
      id:       stepId,
      name:     meta.label ?? type,
      type,
      config:   { ...(meta.defaultConfig ?? {}) },
      position: nextPosition(workflow.steps),
    };

    const steps = [...workflow.steps, newStep];

    // Build edges: connect sequential chain
    const prevId = steps.length > 1 ? steps[steps.length - 2].id : null;
    const newEdge = prevId
      ? { id: `e-${uid()}`, source: prevId, target: stepId }
      : { id: `e-${uid()}`, source: 'trigger', target: stepId };

    update({ steps, edges: [...workflow.edges, newEdge] });
    setSelectedNodeId(stepId);
  }

  // ── Delete step ───────────────────────────────────────────────────────────
  const deleteStep = useCallback((stepId) => {
    setWorkflow(prev => {
      const steps = prev.steps.filter(s => s.id !== stepId);
      // Reconnect edges: find predecessor and successor
      const incoming = prev.edges.find(e => e.target === stepId);
      const outgoing = prev.edges.find(e => e.source === stepId);
      let edges = prev.edges.filter(e => e.source !== stepId && e.target !== stepId);
      if (incoming && outgoing) {
        edges = [...edges, { ...incoming, target: outgoing.target }];
      }
      return { ...prev, steps, edges };
    });
    setIsDirty(true);
    setSelectedNodeId(null);
  }, []);

  // ── Update step config ────────────────────────────────────────────────────
  function updateStep(updatedStep) {
    update({
      steps: workflow.steps.map(s => s.id === updatedStep.id ? updatedStep : s),
    });
  }

  // ── Position updates from canvas drag ─────────────────────────────────────
  function handlePositionChange(changes) {
    const posMap = Object.fromEntries(changes.map(c => [c.id, c.position]));
    update({
      steps: workflow.steps.map(s =>
        posMap[s.id] ? { ...s, position: posMap[s.id] } : s
      ),
    });
  }

  // Resolved selected step
  const selectedStep = workflow.steps.find(s => s.id === selectedNodeId);

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center" style={{ color: 'var(--text-muted)' }}>
        <Loader2 size={20} className="spin" /> &nbsp; Loading workflow…
      </div>
    );
  }

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* ── Toolbar ─────────────────────────────────────────────────────────── */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '0 16px', height: 52,
        background: 'var(--surface)',
        borderBottom: '1px solid var(--border)',
        flexShrink: 0,
      }}>
        <EditableTitle value={workflow.name} onChange={name => update({ name })} />

        {isDirty && (
          <span style={{ fontSize: '.72rem', color: 'var(--text-muted)', background: 'var(--surface-2)', padding: '2px 8px', borderRadius: 9999, border: '1px solid var(--border)' }}>
            unsaved
          </span>
        )}

        <div style={{ flex: 1 }} />

        {!isNew && (
          <Link to={`/workflows/${id}/runs`} style={{ textDecoration: 'none' }}>
            <button className="btn-ghost" style={{ fontSize: '.8rem' }}>
              <Activity size={14} /> Runs
            </button>
          </Link>
        )}

        {!isNew && (
          <button
            className="btn-ghost"
            onClick={toggleActive}
            style={{ fontSize: '.8rem', color: workflow.is_active ? 'var(--green)' : 'var(--text-muted)' }}
          >
            {workflow.is_active
              ? <><ToggleRight size={15} /> Active</>
              : <><ToggleLeft size={15} />  Inactive</>
            }
          </button>
        )}

        <button
          className="btn-primary"
          onClick={save}
          disabled={saving}
        >
          {saving ? <Loader2 size={14} className="spin" /> : <Save size={14} />}
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>

      {/* ── Body ────────────────────────────────────────────────────────────── */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* Left palette */}
        <div style={{
          width: 200, flexShrink: 0,
          background: 'var(--surface)',
          borderRight: '1px solid var(--border)',
          display: 'flex', flexDirection: 'column', gap: 4,
          padding: '14px 10px',
          overflowY: 'auto',
        }}>
          <div style={{ fontSize: '.68rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--text-muted)', marginBottom: 6, paddingLeft: 4 }}>
            Steps
          </div>

          <button
            className="btn-primary"
            onClick={() => setShowStepModal(true)}
            style={{ width: '100%', justifyContent: 'center', marginBottom: 8 }}
          >
            <Plus size={14} /> Add Step
          </button>

          {/* Existing step list */}
          {workflow.steps.map((step, i) => {
            const meta = STEP_TYPES[step.type] ?? {};
            const isSelected = selectedNodeId === step.id;
            return (
              <button
                key={step.id}
                onClick={() => setSelectedNodeId(isSelected ? null : step.id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '7px 10px', borderRadius: 7, width: '100%',
                  background: isSelected ? 'var(--primary-glow)' : 'none',
                  border: `1px solid ${isSelected ? 'var(--primary)' : 'transparent'}`,
                  color: 'var(--text-dim)', cursor: 'pointer', textAlign: 'left',
                  fontSize: '.8rem',
                  transition: 'all .12s',
                }}
                className="hover:bg-[#1E2330]"
              >
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: meta.color ?? 'var(--primary)', flexShrink: 0 }} />
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {i + 1}. {step.name}
                </span>
              </button>
            );
          })}

          {!workflow.steps.length && (
            <p style={{ color: 'var(--text-muted)', fontSize: '.75rem', textAlign: 'center', padding: '20px 0', lineHeight: 1.6 }}>
              No steps yet.<br />Click "Add Step" to get started.
            </p>
          )}
        </div>

        {/* Canvas + logs */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
            <Canvas
              workflow={workflow}
              selectedNodeId={selectedNodeId}
              onSelectNode={setSelectedNodeId}
              onDeleteNode={deleteStep}
              onPositionChange={handlePositionChange}
            />
          </div>

          {/* Logs panel (expandable) */}
          {showLogs && (
            <div style={{ height: 280, flexShrink: 0, borderTop: '1px solid var(--border)', display: 'flex', flexDirection: 'column' }}>
              <div style={{ display: 'flex', alignItems: 'center', padding: '6px 12px', background: 'var(--surface)', borderBottom: '1px solid var(--border)' }}>
                <span style={{ fontSize: '.75rem', fontWeight: 600, color: 'var(--text-dim)', fontFamily: 'IBM Plex Mono' }}>
                  Run Output
                </span>
                <button onClick={() => setShowLogs(false)} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}>
                  <ChevronDown size={14} />
                </button>
              </div>
              <div style={{ flex: 1, overflow: 'hidden' }}>
                <LogsPanel run={activeRun} logs={runLogs} steps={workflow.steps} />
              </div>
            </div>
          )}
        </div>

        {/* Right: step config panel */}
        {selectedStep && (
          <StepConfigPanel
            step={selectedStep}
            onChange={updateStep}
            onClose={() => setSelectedNodeId(null)}
          />
        )}
      </div>

      {/* ── Trigger config ───────────────────────────────────────────────────── */}
      <div style={{ flexShrink: 0 }}>
        <button
          onClick={() => setShowTrigger(p => !p)}
          style={{
            width: '100%', display: 'flex', alignItems: 'center', gap: 8,
            padding: '8px 20px', background: 'var(--surface)',
            borderTop: '1px solid var(--border)', border: 'none',
            cursor: 'pointer', color: 'var(--text-muted)', fontSize: '.78rem',
          }}
          className="hover:bg-[#1E2330] transition-colors"
        >
          {showTrigger ? <ChevronDown size={13} /> : <ChevronUp size={13} />}
          Trigger Configuration
        </button>

        {showTrigger && (
          <TriggerConfig
            workflow={workflow}
            onChange={updated => update({ trigger_type: updated.trigger_type, trigger_config: updated.trigger_config })}
            onManualTrigger={manualTrigger}
            isTriggerLoading={triggerLoading}
          />
        )}
      </div>

      {/* Step picker modal */}
      {showStepModal && (
        <StepModal onAdd={addStep} onClose={() => setShowStepModal(false)} />
      )}

      {/* Toast */}
      {toast && (
        <Toast message={toast.message} type={toast.type} onDismiss={() => setToast(null)} />
      )}
    </div>
  );
}
